import { DirectionalHint, getTheme, Icon, Stack, StackItem, Text, TooltipHost } from '@fluentui/react';
import { getDisplayTitle, TabItem } from './TabList';
import React, { useEffect, useRef } from 'react';
import TabsList from './TabList';
import { IC } from 'components/SVGIcon';
import { StarFilled } from 'kits/IconKit2';
import PresencePill from './PresencePill';
import { useIsFavoritesDropdownVisible } from 'shared-state/directory/hooks';
import { useParams } from 'react-router';

export interface IFavoriteAreasProps {
    useDropdown: boolean;
    items: TabItem[];
    handleAreaGroupClick: (e: React.MouseEvent<HTMLDivElement>, item: TabItem) => void;
    handleAreaClick: (e: React.MouseEvent<HTMLDivElement>, item: TabItem) => void;
    color?: string;
    getPresenceTooltip: (u: number, v: number) => string;
}
const theme = getTheme();

const FavoriteAreas: React.FC<IFavoriteAreasProps> = ({ useDropdown, items, handleAreaGroupClick, handleAreaClick, color, getPresenceTooltip }) => {
    const { areaId: areaIdParam } = useParams<{ areaId?: string }>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isFavoritesDropdownVisible, setIsFavoritesDropdownVisible] = useIsFavoritesDropdownVisible();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsFavoritesDropdownVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isFavoritesDropdownVisible]);

    if (useDropdown) {
        return (
            <div ref={dropdownRef}>
                <Stack>
                    <Stack
                        horizontal
                        verticalAlign="center"
                        styles={{ root: { cursor: 'pointer', marginTop: 10 } }}
                        onClick={() => setIsFavoritesDropdownVisible(!isFavoritesDropdownVisible)}
                    >
                        <Stack horizontal verticalAlign="center" style={{ position: 'absolute', right: 10, top: 60 }}>
                            <StackItem>
                                <IC variant={'custom'} size={24} iconColor={color}>
                                    <StarFilled />
                                </IC>
                            </StackItem>
                            <Icon
                                iconName={'ChevronDownMed'}
                                styles={{
                                    root: {
                                        fontSize: '14px',
                                        marginTop: 2,
                                        marginLeft: 8,
                                        color: color,
                                        cursor: 'pointer',
                                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }
                                }}
                                style={{
                                    transform: isFavoritesDropdownVisible ? 'rotate(180deg)' : 'rotate(0deg)'
                                }}
                            />
                        </Stack>
                    </Stack>

                    {isFavoritesDropdownVisible && (
                        <Stack
                            style={{
                                position: 'absolute',
                                top: 95,
                                right: 5,
                                width: 250,
                                minHeight: 100,
                                padding: 16,
                                zIndex: 100,
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                backgroundColor: theme.palette.neutralLighter,
                                color: '#000',
                                overflow: 'hidden',
                                transform: isFavoritesDropdownVisible ? 'scaleY(1)' : 'scaleY(0)',
                                transformOrigin: 'top',
                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                opacity: isFavoritesDropdownVisible ? 1 : 0.8,
                                transitionProperty: 'transform, opacity'
                            }}
                        >
                            {items.map((it) => {
                                const isSelected = it.key === areaIdParam || false;
                                return (
                                    <Stack
                                        horizontal
                                        verticalAlign="center"
                                        styles={{
                                            root: {
                                                cursor: 'pointer',
                                                height: 50,
                                                width: '100%',
                                                padding: 2,
                                                background: isSelected ? 'rgba(0,0,0,0.2)' : 'transparent',
                                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' }
                                            }
                                        }}
                                        key={it.key}
                                        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                            if (it.groupLevel) {
                                                handleAreaGroupClick(e, it);
                                            } else {
                                                handleAreaClick(e, it);
                                            }
                                            setIsFavoritesDropdownVisible(false);
                                        }}
                                    >
                                        <TooltipHost content={getDisplayTitle(it).tooltipContent} directionalHint={DirectionalHint.topCenter}>
                                            <Stack horizontal verticalAlign="center" styles={{ root: { cursor: 'pointer' } }}>
                                                <IC variant={'custom'} size={24} iconColor={'#000'}>
                                                    <StarFilled />
                                                </IC>
                                                <Stack horizontal style={{ marginTop: 4 }}>
                                                    <Text style={{ marginLeft: 16, marginRight: -46, color: '#000', width: 140, wordBreak: 'break-all' }}>
                                                        {getDisplayTitle(it).truncatedTitle}
                                                    </Text>
                                                    <PresencePill
                                                        usersAndVisitorsCount={it.usersAndVisitorsCount}
                                                        color={'#000'}
                                                        getPresenceTooltip={getPresenceTooltip}
                                                    />
                                                </Stack>
                                            </Stack>
                                        </TooltipHost>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </div>
        );
    }

    // for large laptop, we use tabs
    return (
        <TabsList
            items={items}
            color={color}
            getPresenceTooltip={getPresenceTooltip}
            handleAreaGroupClick={handleAreaGroupClick}
            handleAreaClick={handleAreaClick}
        />
    );
};

export default FavoriteAreas;
